/**
 * Highlights API Service
 * Handles all highlight-related API operations
 */

import apiClient from './client';
import { API_ENDPOINTS } from './endpoints';

class HighlightsService {
  /**
   * Get highlights for a specific user and topic
   * @param {string} userId - User identifier
   * @param {string} topicId - Topic identifier
   * @returns {Promise<Array>} Array of highlights
   */
  async getHighlights(userId, topicId) {
    try {
      if (!userId || !topicId) {
        console.warn('Missing required parameters for getHighlights:', { userId, topicId });
        return [];
      }

      const client = await apiClient.initialize();
      console.log('Fetching highlights with params:', { userId, topicId });

      const response = await client.get(API_ENDPOINTS.highlighters, {
        params: {
          userId,
          topicId
        }
      });

      // Handle different possible response structures
      const responseData = response?.data;
      let highlightsData = [];

      if (Array.isArray(responseData)) {
        highlightsData = responseData;
      } else if (responseData?.data && Array.isArray(responseData.data)) {
        highlightsData = responseData.data;
      }

      // Parse highlightData from string to object and ensure required fields
      return highlightsData
        .filter(highlight => highlight && highlight.id)
        .map(highlight => {
          try {
            const highlightData = typeof highlight.highlightData === 'string'
              ? JSON.parse(highlight.highlightData)
              : highlight.highlightData || {};

            return {
              ...highlight,
              highlightData,
              // Ensure we have the required fields for react-pdf-highlighter
              id: highlight.id,
              userId: highlight.userId || userId,
              topicId: highlight.topicId || topicId
            };
          } catch (e) {
            console.error('Error parsing highlight data:', e, highlight);
            return null;
          }
        })
        .filter(Boolean); // Remove any null entries from failed parsing

    } catch (error) {
      console.error('Error fetching highlights:', {
        error: error.message,
        userId,
        topicId,
        response: error.response?.data
      });
      throw error;
    }
  }

  /**
   * Create a new highlight
   * @param {Object} highlightData - The highlight data
   * @param {string} highlightData.userId - The user ID
   * @param {string} highlightData.topicId - The topic ID
   * @param {Object} highlightData.highlight - The highlight object (position, comment, etc.)
   * @param {string} [highlightData.title] - Optional title for the highlight
   * @param {string} [highlightData.tags] - Optional tags for the highlight
   * @returns {Promise<Object>} The created highlight
   */
  async createHighlight({
    userId,
    topicId,
    highlight,
    title = 'Untitled Highlight',
    tags = ''
  }) {
    try {
      // Create a clean highlight object with only necessary data
      const cleanHighlight = {
        ...highlight,
        // Ensure we're not storing the actual text content
        content: {
          text: '',
          emoji: highlight.content?.emoji || ''
        }
      };

      // Stringify the highlight data for the API
      const payload = {
        userId,
        topicId,
        highlightData: JSON.stringify(cleanHighlight),
        title: title || highlight.comment?.text?.substring(0, 50) || 'Untitled Highlight',
        tags: tags
      };

      const client = await apiClient.initialize();
      const response = await client.post(API_ENDPOINTS.highlighters, payload);

      // Return the parsed highlight data
      return {
        ...response.data,
        highlightData: cleanHighlight // Return the original object, not stringified
      };
    } catch (error) {
      console.error('Error creating highlight:', error);
      throw error;
    }
  }

  /**
   * Update an existing highlight
   * @param {string} highlightId - The ID of the highlight to update
   * @param {Object} updates - The updates to apply to the highlight
   * @returns {Promise<Object>} The updated highlight
   */
  async updateHighlight(highlightId, updates) {
    try {
      // If highlightData is in the updates, make sure it's stringified
      const payload = { ...updates };
      if (payload.highlightData && typeof payload.highlightData !== 'string') {
        payload.highlightData = JSON.stringify(payload.highlightData);
      }

      const client = await apiClient.initialize();
      const response = await client.put(
        `${API_ENDPOINTS.highlighters}/${highlightId}`,
        payload
      );

      // Parse the highlightData back to object
      return {
        ...response.data,
        highlightData: typeof response.data.highlightData === 'string'
          ? JSON.parse(response.data.highlightData)
          : response.data.highlightData
      };
    } catch (error) {
      console.error('Error updating highlight:', error);
      throw error;
    }
  }

  /**
   * Delete a highlight
   * @param {string} highlightId - The ID of the highlight to delete
   * @returns {Promise<void>}
   */
  async deleteHighlight(highlightId) {
    try {
      const client = await apiClient.initialize();
      await client.delete(`${API_ENDPOINTS.highlighters}/${highlightId}`);
    } catch (error) {
      console.error('Error deleting highlight:', error);
      throw error;
    }
  }
}

// Export a singleton instance
const highlightsService = new HighlightsService();
export default highlightsService;
